import React from "react";
import { IconType } from "react-icons";
import {
  MdOutlineSend,
  MdOutlineDoneAll,
  MdOutlineVisibility,
  MdOutlineGroups,
} from "react-icons/md";
import { DashboardData } from "../../types/dashboardType";

interface Props {
  data: DashboardData | null;
  loading: boolean;
}

const formatRate = (n?: number | null) => {
  const v = Number(n) || 0;
  return `${Number.isInteger(v) ? v : v.toFixed(1)}%`;
};

interface Stat {
  title: string;
  value: string;
  hint: string;
  Icon: IconType;
  tone: string; // icon chip colours
  rate?: number; // shows a progress bar
  bar?: string;
}

export default function StatsSection({ data, loading }: Props) {
  const stats: Stat[] = [
    {
      title: "Messages sent",
      value: (data?.totalSent ?? 0).toLocaleString(),
      hint: "All time",
      Icon: MdOutlineSend,
      tone: "bg-blue-50 text-blue-600 ring-blue-600/10",
    },
    {
      title: "Delivery rate",
      value: formatRate(data?.deliveryRate),
      hint: `${(data?.delivered ?? 0).toLocaleString()} delivered`,
      Icon: MdOutlineDoneAll,
      tone: "bg-emerald-50 text-emerald-600 ring-emerald-600/10",
      rate: data?.deliveryRate ?? 0,
      bar: "bg-emerald-500",
    },
    {
      title: "Read rate",
      value: formatRate(data?.readRate),
      hint: `${(data?.read ?? 0).toLocaleString()} read`,
      Icon: MdOutlineVisibility,
      tone: "bg-violet-50 text-violet-600 ring-violet-600/10",
      rate: data?.readRate ?? 0,
      bar: "bg-violet-500",
    },
    {
      title: "Active clients",
      value: (data?.activeContacts ?? 0).toLocaleString(),
      hint: "Contacts you've messaged",
      Icon: MdOutlineGroups,
      tone: "bg-amber-50 text-amber-600 ring-amber-600/10",
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      {stats.map(({ title, value, hint, Icon, tone, rate, bar }) => (
        <div
          key={title}
          className="group relative overflow-hidden bg-white rounded-2xl border border-app-line shadow-card p-5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-card-hover"
        >
          <span className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-main/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="flex items-start justify-between gap-3">
            <p className="text-[11.5px] font-semibold uppercase tracking-[0.08em] text-slate-500">{title}</p>
            <span className={`w-10 h-10 rounded-xl ring-1 ring-inset flex items-center justify-center flex-shrink-0 ${tone}`}>
              <Icon size={19} />
            </span>
          </div>

          {loading ? (
            <div className="mt-2 space-y-2.5">
              <div className="h-8 w-24 rounded-lg bg-app-bg animate-pulse" />
              <div className="h-3 w-32 rounded-full bg-app-bg animate-pulse" />
            </div>
          ) : (
            <>
              <p className="mt-1 text-[30px] leading-10 font-semibold tracking-[-0.025em] text-app-ink tabular-nums">
                {value}
              </p>

              {rate != null ? (
                <div className="mt-3">
                  <div className="h-1.5 rounded-full bg-app-bg overflow-hidden">
                    <div
                      className={`h-full rounded-full ${bar} transition-[width] duration-700`}
                      style={{ width: `${Math.min(Math.max(rate, 0), 100)}%` }}
                    />
                  </div>
                  <p className="mt-2 text-[12px] text-app-muted">{hint}</p>
                </div>
              ) : (
                <p className="mt-3 text-[12px] text-app-muted">{hint}</p>
              )}
            </>
          )}
        </div>
      ))}
    </div>
  );
}
